import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import { CompanyContext } from '../contexts/CompanyContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Image as ImageIcon, Upload, UserPlus, CheckCircle2, ArrowRight, Sparkles, SkipForward, Copy } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';
import { CURRENCIES } from '../lib/currencies';

export default function Onboarding() {
  const { user } = useContext(AuthContext);
  const { company, refresh } = useContext(CompanyContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    name: '', address: '', location: '', industry: '',
    contact_person_name: '', contact_email: '', contact_phone: '',
    logo_url: '', currency: 'AED', currency_symbol: 'AED', arabic_enabled: false,
  });
  const [staff, setStaff] = useState({ name: '', email: '', role: 'MANAGER', phone: '' });
  const [tempResult, setTempResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    // SUPERADMIN uses a different, dedicated flow that creates a Company + Initial Admin in one shot.
    if (user.role === 'SUPERADMIN') {
      navigate('/superadmin/onboard-company', { replace: true });
      return;
    }
    // Non-admins shouldn't be here either.
    if (!['FULL_ACCESS', 'ADMIN', 'CEO'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    if (company) {
      setProfile({
        name: company.name || '',
        address: company.address || '',
        location: company.location || '',
        industry: company.industry || '',
        contact_person_name: company.contact_person_name || user.name || '',
        contact_email: company.contact_email || user.email || '',
        contact_phone: company.contact_phone || '',
        logo_url: company.logo_url || '',
        currency: company.currency || 'AED',
        currency_symbol: company.currency_symbol || 'AED',
        arabic_enabled: !!company.arabic_enabled,
      });
      if (company.onboarding_completed) navigate('/dashboard');
    }
  }, [user, company, navigate]);

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB');
      return;
    }
    const r = new FileReader();
    r.onloadend = () => setProfile(p => ({ ...p, logo_url: r.result }));
    r.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!profile.name) { toast.error('Company / personal name is required'); return; }
    if (!profile.contact_phone) { toast.error('Phone number is required'); return; }
    if (!profile.contact_email) { toast.error('Email is required'); return; }
    if (!profile.location) { toast.error('Location (city / emirate) is required'); return; }
    if (!profile.industry) { toast.error('Please select an industry'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${company.id}`, profile, { withCredentials: true });
      toast.success('Company profile saved');
      await refresh?.();
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const createStaff = async () => {
    if (!staff.name || !staff.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/users`, staff, { withCredentials: true });
      setTempResult(r.data);
      toast.success('Staff account created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
    setSaving(false);
  };

  const finish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${company.id}`, { onboarding_completed: true }, { withCredentials: true });
      await refresh?.();
      toast.success('Onboarding complete. Welcome aboard!');
      navigate('/dashboard');
    } catch (e) {
      toast.error('Could not finalize onboarding');
    }
    setSaving(false);
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const StepBadge = ({ n, label, active, done }) => (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
        done ? 'bg-emerald-500 text-white' : active ? 'bg-amber-500 text-slate-900' : 'bg-slate-200 text-slate-500'
      }`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : n}
      </div>
      <div className={`text-sm ${active || done ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}>{label}</div>
    </div>
  );

  if (!user || !company) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo variant="icon" size={44} className="rounded-lg bg-white border border-slate-200 p-0.5" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Welcome to FM Flexi Pro<span className="text-amber-500">™</span></h1>
            <p className="text-sm text-muted-foreground">Let's set up <strong>{company.name}</strong> in under 2 minutes.</p>
          </div>
        </div>

        <Card className="p-5 mb-6">
          <div className="grid grid-cols-3 gap-4">
            <StepBadge n={1} label="Company Profile" active={step === 1} done={step > 1} />
            <StepBadge n={2} label="First Team Member" active={step === 2} done={step > 2} />
            <StepBadge n={3} label="You're Ready" active={step === 3} />
          </div>
          <div className="h-1 bg-slate-100 rounded mt-4 overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${(step / 3) * 100}%` }} />
          </div>
        </Card>

        {step === 1 && (
          <Card data-testid="onboarding-step-1" className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-600" />Company profile</h2>
            <p className="text-sm text-muted-foreground">This information appears on every PDF report, invoice, and at the top of the app.</p>

            <div className="flex items-start gap-5">
              <div className="w-28 h-28 rounded-xl border-2 border-dashed border-amber-300 bg-white flex items-center justify-center overflow-hidden">
                {profile.logo_url ? <img src={profile.logo_url} alt="" className="max-w-full max-h-full object-contain" /> : <ImageIcon className="w-10 h-10 text-slate-300" />}
              </div>
              <div className="flex-1 space-y-2">
                <Label>Company logo</Label>
                <label data-testid="onb-logo-upload" htmlFor="onb-logo" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-medium cursor-pointer">
                  <Upload className="w-4 h-4" />Upload logo
                </label>
                <input id="onb-logo" type="file" accept="image/png,image/jpeg" onChange={onLogoFile} className="hidden" />
                {profile.logo_url && <Button size="sm" variant="outline" onClick={() => setProfile(p => ({ ...p, logo_url: '' }))}>Remove</Button>}
                <p className="text-xs text-muted-foreground">PNG or JPG · max 500KB · square recommended</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Company / Personal name *</Label><Input data-testid="onb-name" placeholder="e.g. Acme Facilities LLC, or your full name if individual" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
              <div><Label>Contact person *</Label><Input data-testid="onb-contact-person" value={profile.contact_person_name} onChange={(e) => setProfile({ ...profile, contact_person_name: e.target.value })} /></div>
              <div><Label>Phone *</Label><Input data-testid="onb-phone" placeholder="+971 50 …" value={profile.contact_phone} onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="onb-email" type="email" value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} /></div>
              <div><Label>Location (City / Emirate) *</Label><Input data-testid="onb-location" placeholder="e.g. Dubai" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Industry *</Label>
                <Select value={profile.industry} onValueChange={(v) => setProfile({ ...profile, industry: v })}>
                  <SelectTrigger data-testid="onb-industry"><SelectValue placeholder="Select your industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Real Estate">Real Estate</SelectItem>
                    <SelectItem value="Facilities Management">Facilities Management</SelectItem>
                    <SelectItem value="Property Management">Property Management</SelectItem>
                    <SelectItem value="Hospitality">Hospitality (Hotels / Resorts)</SelectItem>
                    <SelectItem value="Retail">Retail / Mall</SelectItem>
                    <SelectItem value="Healthcare">Healthcare / Clinics</SelectItem>
                    <SelectItem value="Education">Education / Schools</SelectItem>
                    <SelectItem value="Industrial">Industrial / Warehousing</SelectItem>
                    <SelectItem value="Government">Government</SelectItem>
                    <SelectItem value="Individual">Individual / Personal</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Address (optional)</Label><Textarea rows={2} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} /></div>
              <div><Label>Currency</Label>
                <Select value={profile.currency} onValueChange={(v) => { const sym = CURRENCIES.find(c => c[0] === v)?.[1] || v; setProfile({ ...profile, currency: v, currency_symbol: sym }); }}>
                  <SelectTrigger data-testid="onb-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(([c, s]) => <SelectItem key={c} value={c}>{c} ({s})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={profile.arabic_enabled} onChange={(e) => setProfile({ ...profile, arabic_enabled: e.target.checked })} className="w-4 h-4 accent-amber-500" />
                Enable Arabic (RTL) interface for all users in your company
              </label>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={finish} disabled={saving}><SkipForward className="w-4 h-4 mr-1" />Skip setup</Button>
              <Button data-testid="onb-next-1" onClick={saveProfile} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? 'Saving…' : <>Continue<ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="onboarding-step-2" className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2"><UserPlus className="w-5 h-5 text-amber-600" />Add your first team member</h2>
            <p className="text-sm text-muted-foreground">Create an account for a Manager, Supervisor, or Accountant. They will receive a one-time temporary password to log in and set their own.</p>

            {!tempResult ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Full name *</Label><Input data-testid="onb-staff-name" value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input data-testid="onb-staff-email" type="email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} /></div>
                  <div><Label>Role</Label>
                    <Select value={staff.role} onValueChange={(v) => setStaff({ ...staff, role: v })}>
                      <SelectTrigger data-testid="onb-staff-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANAGER">Manager</SelectItem>
                        <SelectItem value="CS_SUPERVISOR">Customer Service Supervisor</SelectItem>
                        <SelectItem value="TECHNICIAN">Technician / Engineer</SelectItem>
                        <SelectItem value="ACCOUNTANT">Accountant</SelectItem>
                        <SelectItem value="TENANT">Tenant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Phone (optional)</Label><Input value={staff.phone} onChange={(e) => setStaff({ ...staff, phone: e.target.value })} /></div>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(3)}><SkipForward className="w-4 h-4 mr-1" />Do this later</Button>
                  <Button data-testid="onb-create-staff" onClick={createStaff} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                    {saving ? 'Creating…' : 'Create account'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2"><CheckCircle2 className="w-5 h-5" />Account created</div>
                  <p className="text-xs text-emerald-900">Copy this one-time password now — it will not be shown again.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase text-slate-500">Email</Label>
                    <div className="flex items-center gap-1"><code className="flex-1 px-3 py-2 rounded bg-slate-100 text-sm font-mono">{tempResult.user?.email}</code>
                    <Button size="sm" variant="outline" onClick={() => copy(tempResult.user?.email)}><Copy className="w-3 h-3" /></Button></div></div>
                  <div><Label className="text-xs uppercase text-slate-500">Temp password</Label>
                    <div className="flex items-center gap-1"><code data-testid="onb-temp-pwd" className="flex-1 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-900 font-mono font-bold">{tempResult.temp_password}</code>
                    <Button size="sm" variant="outline" onClick={() => copy(tempResult.temp_password)}><Copy className="w-3 h-3" /></Button></div></div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button data-testid="onb-next-2" onClick={() => setStep(3)} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                    Continue<ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="onboarding-step-3" className="p-8 text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-slate-900" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">You're all set, {user.name?.split(' ')[0] || 'Admin'}!</h2>
            <p className="text-slate-600 max-w-md mx-auto">Your company is configured and ready. From here you can add buildings, assets, staff, and start tracking complaints and PPM tasks.</p>
            <div className="grid grid-cols-2 gap-3 pt-3 max-w-md mx-auto text-left">
              {[
                { label: 'Add buildings', path: '/buildings' },
                { label: 'Register assets', path: '/assets' },
                { label: 'Invite more staff', path: '/users' },
                { label: 'Explore AI insights', path: '/ai-insights' },
              ].map(q => (
                <button key={q.path} className="p-3 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50 text-sm text-slate-800 text-left" onClick={() => { finish(); navigate(q.path); }}>
                  {q.label} <ArrowRight className="w-3 h-3 inline ml-1" />
                </button>
              ))}
            </div>
            <Button data-testid="onb-finish" onClick={finish} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900 mt-3">
              {saving ? 'Finalizing…' : 'Go to dashboard'}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
